import React, { FC, useCallback, useMemo, useState } from 'react';
import './AddCopyPopup.scss';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import ModalDialog from '../../shared/components/modal/ModalDialog';
import { useOrderLists } from '../../shared/common/useOrderLists';
import { AvailablePricesSelector } from '../../shared/common/AvailablePricesSelector/AvailablePricesSelector';
import { ProductOut } from '../../store/api';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { Price, SubscriptionPeriodType } from '../../../models/Article';
import { SelectionListDropDown } from '../../shared/common/SelectionListDropDown';
import { UpdateProductOptions } from '../../store/products/actionsCart';
import { Trans, useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useProductSubscriptionPeriodsV2 } from '../../api/product-details/useProductSubscriptionPeriodsV2';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { Orderlist } from '../../../models/OrderList';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';

type Props = {
    hidePopup: () => void;
    product: ProductOut;
};

const formatDateByMonth = (showYear: boolean, date: string = '', t: TFunction) => {
    if (showYear) {
        return t('dateFormat.dateWithMonth', {
            val: new Date(date)
        });
    }
    return t('dateFormat.dateWithoutYear', {
        val: new Date(date)
    });
};

export const AddCopyPopup: FC<Props> = ({ hidePopup, product }) => {
    const { t } = useTranslation();
    const { orderlists, createNewList } = useOrderLists();
    const [selectedPrice, setSelectedPrice] = useState<Price | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    const { data, isLoading } = useProductSubscriptionPeriodsV2(product.id ?? '');
    const addToProductOptions = useMemo((): UpdateProductOptions => {
        return {
            id: product.id!,
            title: product.title,
            price: selectedPrice
                ? {
                      usageLimit: selectedPrice.usageLimit,
                      termLimit: selectedPrice.termLimit,
                      lendingModel: selectedPrice.lendingModel
                  }
                : {
                      usageLimit: data?.currentPeriod?.availablePrices?.[0]?.usageLimit,
                      termLimit: data?.currentPeriod?.availablePrices?.[0]?.termLimit,
                      lendingModel: data?.currentPeriod?.availablePrices?.[0]?.lendingModel
                  },
            targetSubscriptionPeriodType: SubscriptionPeriodType.CURRENT
        };
    }, [data, product.id, product.title, selectedPrice]);

    const { addToList } = useAddToList({ callback: hidePopup });
    const { addToCart } = useAddToCart({ callback: hidePopup }); // note: we don't have to invalidate the products call, since we don't show a "items in cart" icon on this page

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const handleListClick = useCallback(
        async (list: Orderlist) => {
            await changePrice({ options: addToProductOptions, target: ListActionTarget.List, listId: list.id });
        },
        [addToProductOptions, changePrice]
    );

    const handleCartClick = useCallback(async () => {
        setLoading(true);
        await changePrice({ options: addToProductOptions, target: ListActionTarget.Cart });
        setLoading(false);
    }, [addToProductOptions, changePrice]);

    if (isLoading || !data) {
        return (
            <LoadingWrapper>
                <LoadingOverlay loading={isLoading} />
            </LoadingWrapper>
        );
    }

    const {
        renewalDate,
        currentPeriod: {
            availablePrices,
            subscriptionPeriod: { startDate, endDate, lengthInDays }
        }
    } = data;

    const selectedProduct = {
        ...product,
        price: selectedPrice ?? availablePrices[0],
        availablePrices
    };

    return (
        <ModalDialog
            title={t('components.addCopyPopup.copyPopupHeader')}
            show={true}
            boldHeader={false}
            titleClassName="popup-title"
            onCloseButtonClick={() => hidePopup()}
        >
            <div className="manage-subscription-add-copy-content">
                <div>
                    {t('components.addCopyPopup.copyPopupSubtitle')} <br />
                    <span style={{ whiteSpace: 'nowrap' }}>
                        <Trans
                            i18nKey="components.addCopyPopup.renewalDate"
                            values={{ date: formatDateByMonth(true, renewalDate, t) }}
                        />
                    </span>
                </div>

                <div>
                    <Trans
                        i18nKey={'components.addCopyPopup.currentYear'}
                        count={lengthInDays}
                        values={{
                            startDate: formatDateByMonth(false, startDate, t),
                            endDate: formatDateByMonth(false, endDate, t)
                        }}
                        components={{
                            strong: <strong />
                        }}
                    />
                </div>

                <div className="price-selector">
                    <AvailablePricesSelector
                        product={selectedProduct as any}
                        onSelectPrice={nextPrice => setSelectedPrice(nextPrice)}
                        target={ListActionTarget.Cart}
                        disabled={false}
                        showSelectedPrice={true}
                    />
                </div>

                <div className="copy-popup-footer">
                    <Button
                        theme={ButtonTheme.POSITIVE_INVERT}
                        onClick={() => hidePopup()}
                        text={t('cancel')}
                        size={ButtonSize.LARGE}
                    />

                    <SelectionListDropDown
                        size={ButtonSize.LARGE}
                        theme={ButtonTheme.POSITIVE}
                        label={t('addToList')}
                        lists={orderlists}
                        onCreateNewListClicked={createNewList}
                        onListClick={handleListClick}
                        disabled={loading}
                    />
                    <Button
                        size={ButtonSize.LARGE}
                        theme={ButtonTheme.POSITIVE}
                        icon="shopping_cart"
                        text={t('addToCart')}
                        onClick={handleCartClick}
                        disabled={loading}
                        isLoading={loading}
                    />
                </div>
            </div>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </ModalDialog>
    );
};
